import { useState } from "react";
import { ghostBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import type { QcChecklistCategory, QcChecklistItem } from "@/lib/manufacturing/builderAssistantChecklistClient";
import {
  moveQcChecklistItem,
  renameQcChecklistItem,
  reorderQcChecklist,
  setQcItemFailureNote,
} from "@/lib/manufacturing/qcChecklistArrangeClient";
import { setQcItemFailurePhoto, uploadQcChecklistExample, ownerQcChecklistDecision, requestQcChecklistArchive } from "@/lib/manufacturing/builderAssistantChecklistClient";
import { loadQcExample } from "@/lib/manufacturing/qcChecklistPhotoRead";
import { QcStoredPhotoLink } from "@/components/manufacturing/qc/QcStoredPhotoLink";
import { moveChecklistId } from "@/lib/manufacturing/qcChecklistOrder";

const inputClass =
  "w-full rounded-md border border-gray-300 bg-white px-2 py-1 text-sm dark:border-zinc-600 dark:bg-zinc-900";

export function BuilderAssistantChecklistItemEdit({
  item,
  itemIds,
  categories,
  isOwner,
  notesLocked,
  run,
}: {
  item: QcChecklistItem;
  itemIds: string[];
  categories: QcChecklistCategory[];
  isOwner: boolean;
  notesLocked: boolean;
  run: (task: Promise<{ ok: boolean; error?: string }>) => void;
}) {
  const [title, setTitle] = useState(item.title);
  const [description, setDescription] = useState(item.description);
  const [moveTo, setMoveTo] = useState("");
  const [newSection, setNewSection] = useState("");

  function move(direction: -1 | 1) {
    const ids = moveChecklistId(itemIds, item.id, direction);
    if (ids) void run(reorderQcChecklist("item", ids));
  }

  function saveWording() {
    const nextTitle = title.trim();
    const nextDescription = description.trim();
    if (!nextTitle || (nextTitle === item.title && nextDescription === item.description)) return;
    void run(renameQcChecklistItem(item.id, nextTitle, nextDescription));
  }

  function moveItem() {
    if (moveTo === "__new") {
      if (!newSection.trim()) return;
      void run(moveQcChecklistItem(item.id, "", newSection.trim()));
      return;
    }
    if (!moveTo || moveTo === item.category_id) return;
    void run(moveQcChecklistItem(item.id, moveTo));
  }

  return (
    <li className="space-y-2 rounded-lg border border-gray-200 p-2 dark:border-zinc-700">
      <div className="flex flex-wrap gap-2">
        {item.status !== "active" ? (
          <span className="text-xs text-amber-800 dark:text-amber-200">
            {item.status === "tentative" ? "Pending owner" : "Removal requested"}
          </span>
        ) : null}
        <button type="button" className={ghostBtnClass} disabled={itemIds[0] === item.id} onClick={() => move(-1)}>
          Move up
        </button>
        <button type="button" className={ghostBtnClass} disabled={itemIds[itemIds.length - 1] === item.id} onClick={() => move(1)}>
          Move down
        </button>
      </div>
      <label className="block text-xs text-gray-600 dark:text-zinc-400">
        Item
        <input className={`${inputClass} mt-1`} value={title} onChange={(event) => setTitle(event.target.value)} onBlur={saveWording} />
      </label>
      <label className="block text-xs text-gray-600 dark:text-zinc-400">
        Details
        <textarea className={`${inputClass} mt-1`} rows={2} value={description} onChange={(event) => setDescription(event.target.value)} onBlur={saveWording} />
      </label>
      {item.has_example || item.example_url ? <QcStoredPhotoLink label="Example" load={() => loadQcExample(item.id)} /> : null}
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs text-gray-600 dark:text-zinc-400">
          Move to
          <select className={`${inputClass} mt-1`} value={moveTo} aria-label={`Move ${item.title}`} onChange={(event) => setMoveTo(event.target.value)}>
            <option value="">Choose a section</option>
            {categories.filter((row) => row.id !== item.category_id && row.status !== "archived").map((row) => (
              <option key={row.id} value={row.id}>{row.name}</option>
            ))}
            <option value="__new">Create new</option>
          </select>
        </label>
        {moveTo === "__new" ? (
          <input className={inputClass} value={newSection} placeholder="New section" aria-label="New section name" onChange={(event) => setNewSection(event.target.value)} />
        ) : null}
        <button type="button" className={ghostBtnClass} disabled={!moveTo} onClick={moveItem}>
          Move
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-zinc-400">
          <input type="checkbox" checked={item.failure_photo_required} onChange={(event) => void run(setQcItemFailurePhoto(item.id, event.target.checked))} />
          Failure needs a photo
        </label>
        <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-zinc-400" title={notesLocked ? "Every failure already needs a description." : undefined}>
          <input
            type="checkbox"
            checked={notesLocked || item.failure_note_required}
            disabled={notesLocked}
            onChange={(event) => void run(setQcItemFailureNote(item.id, event.target.checked))}
          />
          Failure needs a description
        </label>
        <label className={`${ghostBtnClass} inline-flex cursor-pointer`}>
          Example photo
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = "";
              if (file) void run(uploadQcChecklistExample(item.id, file));
            }}
          />
        </label>
        {isOwner && item.status === "tentative" ? (
          <button type="button" className={ghostBtnClass} onClick={() => void run(ownerQcChecklistDecision({ action: "finalize", kind: "item", id: item.id }))}>
            Make permanent
          </button>
        ) : null}
        {item.status !== "archive_requested" ? (
          <button type="button" className={ghostBtnClass} onClick={() => void run(requestQcChecklistArchive("item", item.id))}>
            Schedule removal
          </button>
        ) : null}
        {isOwner && item.status === "archive_requested" ? (
          <>
            <button type="button" className={ghostBtnClass} onClick={() => void run(ownerQcChecklistDecision({ action: "archive", kind: "item", id: item.id }))}>
              Remove
            </button>
            <button type="button" className={ghostBtnClass} onClick={() => void run(ownerQcChecklistDecision({ action: "keep", kind: "item", id: item.id }))}>
              Keep
            </button>
          </>
        ) : null}
      </div>
    </li>
  );
}
